import React, { useEffect } from 'react';
import { Input, InputGroup, InputGroupText } from 'reactstrap';
import classNames from 'classnames';

import { InputProps } from '../props';
import { stripDecimalString } from '../libs/numericFormatters';
import ReadOnly from './ReadOnly';

interface ComponentProps extends InputProps {
  noDecimals?: boolean;
  className?: string;
}

const Currency: React.FC<ComponentProps> = ({
  id,
  value,
  disabled = false,
  onChange,
  setFieldValue,
  noDecimals = false,
  className,
}) => {
  const readonlyCurrencyFormatter = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: noDecimals ? 0 : 2,
    maximumFractionDigits: noDecimals ? 0 : 2,
  });

  const formatCurrency = (value: string): string => parseFloat(value.trim() || '0').toFixed(2);

  const formatReadOnly = (value: string) => {
    if (value) {
      return readonlyCurrencyFormatter.format(parseFloat(value));
    }
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const formattedInput = stripDecimalString(value);

    setFieldValue?.(id, formattedInput);
    onChange?.(id, formattedInput);
  };

  const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    event.preventDefault();
    const { value } = event.target;
    setFieldValue?.(id, formatCurrency(value));
  };

  useEffect(() => {
    if (!disabled) setFieldValue?.(id, formatCurrency(value?.toString() ?? ''));
  }, []);

  const inputClassName = classNames([className], {
    'ms-1': className === undefined,
  });

  return (
    <>
      {!disabled ? (
        <InputGroup>
          <InputGroupText>$</InputGroupText>
          <Input
            id={id}
            name={id}
            value={value ?? ''}
            disabled={disabled}
            className={inputClassName}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            data-cy={`${id}-currency`}
          />
        </InputGroup>
      ) : (
        <ReadOnly
          className={className}
          type="readonly"
          id={id}
          name={id}
          value={formatReadOnly(value?.toString() ?? '0')}
        />
      )}
    </>
  );
};

export default Currency;
